/**
 * Pure-CPU geometry ops backing the export UV projector (`uvProject.ts`):
 * unindex → project → tangents → re-weld, mirroring the prototype's export
 * path (three.js toNonIndexed / computeMikkTSpaceTangents / mergeVertices)
 * without three.js.
 *
 * Tangents here are Lengyel per-triangle tangents (accumulated across shared
 * verts, Gram-Schmidt orthonormalized, w = handedness) — a stand-in for the
 * prototype's MikkTSpace pass: equivalent on the planar charts box projection
 * produces, but not bit-identical to MikkTSpace on curved welds.
 *
 * All ops are strictly per-geometry; submesh/material ranges are the caller's
 * concern (see uvProject.ts).
 */

/** Triangle soup: 3 verts per triangle, no index buffer. */
export interface UnindexedGeometry {
    positions: Float32Array;
    normals: Float32Array;
    uvs: Float32Array | null;
    triCount: number;
}

/** Attribute quantization step shared by all welds here: 1e-4 per component. */
export const KEY_QUANT = 1e4;

/**
 * Expand indexed attributes into a triangle soup. `indices` may be null for
 * geometry that is already unindexed — the arrays are copied either way, so
 * the result never aliases the input.
 */
export function toNonIndexed(
    positions: Float32Array,
    normals: Float32Array,
    uvs: Float32Array | null,
    indices: ArrayLike<number> | null,
): UnindexedGeometry {
    // Every op here is exported standalone, so each states its own shape
    // contract rather than trusting the one caller that validates (see
    // buildProjectedUV). A misaligned buffer that slips through silently
    // produces truncated/shifted geometry — no exception, no visible error
    // until the exported mesh is wrong.
    if (positions.length % 3 !== 0) {
        throw new Error(`toNonIndexed: positions must be whole vec3s, got length ${positions.length}`);
    }
    if (normals.length !== positions.length) {
        throw new Error(`toNonIndexed: normals length ${normals.length} != positions length ${positions.length}`);
    }
    if (uvs && uvs.length / 2 !== positions.length / 3) {
        throw new Error(`toNonIndexed: uvs length ${uvs.length} does not match ${positions.length / 3} vertices`);
    }
    if (indices && indices.length % 3 !== 0) {
        throw new Error(`toNonIndexed: indices must be triangles (length % 3 === 0), got ${indices.length}`);
    }
    if (!indices) {
        if (positions.length % 9 !== 0) {
            throw new Error(`toNonIndexed: unindexed positions must be whole triangles (length % 9 === 0), got ${positions.length}`);
        }
        return {
            positions: positions.slice(),
            normals: normals.slice(),
            uvs: uvs ? uvs.slice() : null,
            triCount: positions.length / 9,
        };
    }
    const count = indices.length;
    const outPos = new Float32Array(count * 3);
    const outNor = new Float32Array(count * 3);
    const outUv = uvs ? new Float32Array(count * 2) : null;
    for (let i = 0; i < count; i++) {
        const src = indices[i];
        outPos[i * 3] = positions[src * 3];
        outPos[i * 3 + 1] = positions[src * 3 + 1];
        outPos[i * 3 + 2] = positions[src * 3 + 2];
        outNor[i * 3] = normals[src * 3];
        outNor[i * 3 + 1] = normals[src * 3 + 1];
        outNor[i * 3 + 2] = normals[src * 3 + 2];
        if (outUv && uvs) {
            outUv[i * 2] = uvs[src * 2];
            outUv[i * 2 + 1] = uvs[src * 2 + 1];
        }
    }
    return { positions: outPos, normals: outNor, uvs: outUv, triCount: count / 3 };
}

/**
 * Lengyel per-triangle tangents for an UNINDEXED mesh, vec4 with
 * w = handedness (±1). Accumulation happens per welded (pos, normal, uv) key
 * so a vertex shared by several triangles gets one averaged tangent — the
 * unindexed input carries no adjacency, and per-corner tangents would put a
 * lighting seam on every triangle edge. Output is unindexed, 4 floats per
 * input vertex.
 */
export function computeTangents(
    positions: Float32Array,
    normals: Float32Array,
    uvs: Float32Array,
): Float32Array {
    if (positions.length % 9 !== 0) {
        throw new Error(`computeTangents: positions must be unindexed triangles (length % 9 === 0), got ${positions.length}`);
    }
    if (normals.length !== positions.length) {
        throw new Error(`computeTangents: normals length ${normals.length} != positions length ${positions.length}`);
    }
    if (uvs.length / 2 !== positions.length / 3) {
        throw new Error(`computeTangents: uvs length ${uvs.length} does not match ${positions.length / 3} vertices`);
    }
    const count = positions.length / 3;
    const triCount = count / 3;

    // Weld verts on all three attributes: tangent accumulation must not cross
    // a UV seam (that would smooth away the handedness flip the projector's
    // signed mode creates).
    const idOf = new Int32Array(count);
    const keyToId = new Map<string, number>();
    let nextId = 0;
    for (let i = 0; i < count; i++) {
        const key =
            Math.round(positions[i * 3] * KEY_QUANT) + "_" + Math.round(positions[i * 3 + 1] * KEY_QUANT) + "_" + Math.round(positions[i * 3 + 2] * KEY_QUANT) + "_"
            + Math.round(normals[i * 3] * KEY_QUANT) + "_" + Math.round(normals[i * 3 + 1] * KEY_QUANT) + "_" + Math.round(normals[i * 3 + 2] * KEY_QUANT) + "_"
            + Math.round(uvs[i * 2] * KEY_QUANT) + "_" + Math.round(uvs[i * 2 + 1] * KEY_QUANT);
        let id = keyToId.get(key);
        if (id === undefined) { id = nextId++; keyToId.set(key, id); }
        idOf[i] = id;
    }

    const tan = new Float32Array(nextId * 3); // accumulated sdir
    const bitan = new Float32Array(nextId * 3); // accumulated tdir
    for (let t = 0; t < triCount; t++) {
        const a = 3 * t, b = 3 * t + 1, c = 3 * t + 2;
        const x1 = positions[b * 3] - positions[a * 3];
        const y1 = positions[b * 3 + 1] - positions[a * 3 + 1];
        const z1 = positions[b * 3 + 2] - positions[a * 3 + 2];
        const x2 = positions[c * 3] - positions[a * 3];
        const y2 = positions[c * 3 + 1] - positions[a * 3 + 1];
        const z2 = positions[c * 3 + 2] - positions[a * 3 + 2];
        const s1 = uvs[b * 2] - uvs[a * 2];
        const t1 = uvs[b * 2 + 1] - uvs[a * 2 + 1];
        const s2 = uvs[c * 2] - uvs[a * 2];
        const t2 = uvs[c * 2 + 1] - uvs[a * 2 + 1];
        const denom = s1 * t2 - s2 * t1;
        // Degenerate UV mapping contributes nothing (three.js skips via !isFinite(r)).
        if (Math.abs(denom) < 1e-12) continue;
        const r = 1 / denom;
        const sdx = (t2 * x1 - t1 * x2) * r, sdy = (t2 * y1 - t1 * y2) * r, sdz = (t2 * z1 - t1 * z2) * r;
        const tdx = (s1 * x2 - s2 * x1) * r, tdy = (s1 * y2 - s2 * y1) * r, tdz = (s1 * z2 - s2 * z1) * r;
        for (let k = 0; k < 3; k++) {
            const id = idOf[3 * t + k];
            tan[id * 3] += sdx; tan[id * 3 + 1] += sdy; tan[id * 3 + 2] += sdz;
            bitan[id * 3] += tdx; bitan[id * 3 + 1] += tdy; bitan[id * 3 + 2] += tdz;
        }
    }

    const out = new Float32Array(count * 4);
    for (let i = 0; i < count; i++) {
        const id = idOf[i];
        const nx = normals[i * 3], ny = normals[i * 3 + 1], nz = normals[i * 3 + 2];
        const tx = tan[id * 3], ty = tan[id * 3 + 1], tz = tan[id * 3 + 2];
        // Gram-Schmidt: t' = normalize(t - n * (n . t))
        const nDotT = nx * tx + ny * ty + nz * tz;
        let ox = tx - nx * nDotT, oy = ty - ny * nDotT, oz = tz - nz * nDotT;
        const len = Math.hypot(ox, oy, oz);
        if (len > 1e-9) {
            ox /= len; oy /= len; oz /= len;
        } else {
            // Never accumulated (all-degenerate UVs) or parallel to the
            // normal: fall back to a deterministic tangent built from the
            // world axis least aligned with n, so output stays valid vec4s.
            const ax = Math.abs(nx), ay = Math.abs(ny), az = Math.abs(nz);
            let rx = 0, ry = 0, rz = 0;
            if (ax <= ay && ax <= az) rx = 1; else if (ay <= az) ry = 1; else rz = 1;
            ox = ry * nz - rz * ny; oy = rz * nx - rx * nz; oz = rx * ny - ry * nx;
            const fl = Math.hypot(ox, oy, oz) || 1;
            ox /= fl; oy /= fl; oz /= fl;
        }
        // Handedness from the RAW accumulated tangent/bitangent (Lengyel):
        // w = sign((n x t) . b), with +1 for the degenerate case.
        const cx = ny * tz - nz * ty, cy = nz * tx - nx * tz, cz = nx * ty - ny * tx;
        const w = (cx * bitan[id * 3] + cy * bitan[id * 3 + 1] + cz * bitan[id * 3 + 2]) < 0 ? -1 : 1;
        out[i * 4] = ox; out[i * 4 + 1] = oy; out[i * 4 + 2] = oz; out[i * 4 + 3] = w;
    }
    return out;
}

/** Unindexed attribute set accepted by {@link weldByAttributes}. */
export interface WeldableGeometry {
    positions: Float32Array;
    normals: Float32Array;
    uvs: Float32Array;
    tangents?: Float32Array;
}

/** Indexed result of {@link weldByAttributes}. */
export interface WeldedGeometry {
    indices: Uint32Array;
    positions: Float32Array;
    normals: Float32Array;
    uvs: Float32Array;
    tangents?: Float32Array;
}

/**
 * Weld an unindexed geometry into an indexed one by EXACT key over ALL
 * attributes, quantized at 1e-4 per component. Keying on every attribute is
 * the point: position-only welding would merge the vertex splits along UV
 * seams that the projector just created, collapsing each seam to a single
 * vertex with one arbitrary UV. Verts that agree on every attribute merge;
 * seam verts (same position, different uv/normal/tangent) stay split.
 * First occurrence wins for the stored attribute values.
 */
export function weldByAttributes(g: WeldableGeometry): WeldedGeometry {
    if (g.positions.length % 3 !== 0) {
        throw new Error(`weldByAttributes: positions must be whole vec3s, got length ${g.positions.length}`);
    }
    const count = g.positions.length / 3;
    if (g.normals.length !== g.positions.length) {
        throw new Error(`weldByAttributes: normals length ${g.normals.length} != positions length ${g.positions.length}`);
    }
    if (g.uvs.length / 2 !== count) {
        throw new Error(`weldByAttributes: uvs length ${g.uvs.length} does not match ${count} vertices`);
    }
    if (g.tangents && g.tangents.length / 4 !== count) {
        throw new Error(`weldByAttributes: tangents length ${g.tangents.length} does not match ${count} vertices`);
    }
    const hasTangents = g.tangents !== undefined;
    const indices = new Uint32Array(count);
    const keyToId = new Map<string, number>();
    const pos: number[] = [], nor: number[] = [], uv: number[] = [], tang: number[] = [];
    let nextId = 0;
    for (let i = 0; i < count; i++) {
        let key =
            Math.round(g.positions[i * 3] * KEY_QUANT) + "_" + Math.round(g.positions[i * 3 + 1] * KEY_QUANT) + "_" + Math.round(g.positions[i * 3 + 2] * KEY_QUANT) + "_"
            + Math.round(g.normals[i * 3] * KEY_QUANT) + "_" + Math.round(g.normals[i * 3 + 1] * KEY_QUANT) + "_" + Math.round(g.normals[i * 3 + 2] * KEY_QUANT) + "_"
            + Math.round(g.uvs[i * 2] * KEY_QUANT) + "_" + Math.round(g.uvs[i * 2 + 1] * KEY_QUANT);
        if (hasTangents) {
            const tg = g.tangents!;
            key += "_" + Math.round(tg[i * 4] * KEY_QUANT) + "_" + Math.round(tg[i * 4 + 1] * KEY_QUANT)
                + "_" + Math.round(tg[i * 4 + 2] * KEY_QUANT) + "_" + Math.round(tg[i * 4 + 3] * KEY_QUANT);
        }
        let id = keyToId.get(key);
        if (id === undefined) {
            id = nextId++;
            keyToId.set(key, id);
            pos.push(g.positions[i * 3], g.positions[i * 3 + 1], g.positions[i * 3 + 2]);
            nor.push(g.normals[i * 3], g.normals[i * 3 + 1], g.normals[i * 3 + 2]);
            uv.push(g.uvs[i * 2], g.uvs[i * 2 + 1]);
            if (hasTangents) {
                const tg = g.tangents!;
                tang.push(tg[i * 4], tg[i * 4 + 1], tg[i * 4 + 2], tg[i * 4 + 3]);
            }
        }
        indices[i] = id;
    }
    const result: WeldedGeometry = {
        indices,
        positions: new Float32Array(pos),
        normals: new Float32Array(nor),
        uvs: new Float32Array(uv),
    };
    if (hasTangents) result.tangents = new Float32Array(tang);
    return result;
}
